import { Box, Stack, Typography, styled } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { type KeyboardEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useHomeVisitLetterhead,
  useHomecarePatients,
} from '@/apps/latest/features/home-visit/useHomecare'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  scaledFontSize,
} from '@/shared/styles/typography'

/**
 * sidebar IconBar active='homecare' 時の context pane = 在宅の患者 (登録した人だけ)。
 *
 * 行を押すと本文に患者のページ (/homecare/patients/:id) が出て、一覧はここに残る (やり取りと同じ形)。
 * 訪問のページ (/homecare/visits/:id) を開いている間は、その訪問の患者の行を選んだままにする。
 * 在宅の患者は店に数人なので、検索の行は持たない。
 */

// 文字サイズモード追従 (ADR-0019): サイドバーに載る文字は幅と一緒に拡大する。
const Section = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  fontSize: scaledFontSize('bodySm')(theme),
  color: colorTokens.fg.default,
}))

const Heading = styled(Typography)(({ theme }) => ({
  color: colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  fontWeight: FONT_WEIGHT.medium,
  marginBottom: 8,
}))

const EmptyRow = styled(Box)(({ theme }) => ({
  padding: '5px 8px',
  fontSize: scaledFontSize('tag')(theme),
  color: colorTokens.fg.muted,
}))

// 「在宅の患者を登録」の行 (マニュアルの「新しい質問」と同じ字格)
const ActionRow = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  padding: '5px 8px',
  borderRadius: RADIUS.sm,
  cursor: 'pointer',
  fontSize: scaledFontSize('tag')(theme),
  color: active ? colorTokens.fg.default : colorTokens.fg.muted,
  background: active ? colorTokens.bg.muted : 'transparent',
  '&:hover': active ? undefined : { background: colorTokens.bg.muted },
}))

// 患者の行は患者を指すデータ行なので、患者リスト・やり取りの行と同じ字格にする
const PatientRow = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})<{ active: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  backgroundColor: active ? colorTokens.bg.muted : undefined,
  '&:hover': active ? undefined : { backgroundColor: colorTokens.bg.muted },
  '&:focus-visible': {
    outline: 'none',
    boxShadow: colorTokens.focus.ringNeutral,
  },
}))

const formatMonthDay = (iso: string) => {
  const d = new Date(iso)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

interface Props {
  onNavigate?: () => void
}

export const HomecarePane = ({ onNavigate }: Props) => {
  const navigate = useNavigate()
  const { patientId, requestId } = useParams<{
    patientId?: string
    requestId?: string
  }>()
  const { data: patients, isLoading } = useHomecarePatients()
  const { data: letterhead } = useHomeVisitLetterhead(requestId ?? null)
  const activePatientId = patientId ?? letterhead?.patient.id

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }
  const rowProps = (path: string) => ({
    role: 'button',
    tabIndex: 0,
    onClick: () => go(path),
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      go(path)
    },
  })

  return (
    <Section sx={{ pt: 1.5, overflow: 'auto' }}>
      <ActionRow
        active={!activePatientId && !requestId}
        {...rowProps('/homecare')}
      >
        <Stack direction="row" alignItems="center" gap={0.75}>
          <AddIcon sx={{ fontSize: ICON_SIZE.sm }} />
          在宅の患者を登録
        </Stack>
      </ActionRow>

      <Heading sx={{ mt: 2 }}>在宅の患者</Heading>
      {isLoading ? null : patients && patients.length > 0 ? (
        <Stack spacing={0.25}>
          {patients.map((patient) => (
            <PatientRow
              key={patient.id}
              active={patient.id === activePatientId}
              aria-current={patient.id === activePatientId ? 'true' : undefined}
              {...rowProps(`/homecare/patients/${patient.id}`)}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: scaledFontSize('label'),
                  fontWeight: FONT_WEIGHT.medium,
                  color: colorTokens.fg.default,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {patient.name}
              </Typography>
              {patient.last_visited_at && (
                <Typography
                  component="span"
                  title="前回の訪問"
                  sx={{
                    flexShrink: 0,
                    fontSize: scaledFontSize('tag'),
                    color: colorTokens.fg.faint,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatMonthDay(patient.last_visited_at)}
                </Typography>
              )}
            </PatientRow>
          ))}
        </Stack>
      ) : (
        <EmptyRow>(なし)</EmptyRow>
      )}
    </Section>
  )
}
